import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useAuth } from "@/lib/auth";
import {
  checkout, paymentStatus, agnesChat, agnesListSessions, agnesGetSession,
  agnesDeleteSession, agnesGetPantry, agnesGetShopping, agnesGetWhisper,
  agnesGetGarden, agnesSetWhisperEmail,
} from "@/lib/api";
import { AuthForm } from "@/components/site/AuthForm";
import { Overline, PrimaryButton } from "@/components/site/Primitives";
import { PantryDrawer } from "@/components/agnes/PantryDrawer";
import { MealPlans } from "@/components/agnes/MealPlans";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { IMAGES } from "@/lib/constants";
import { toast } from "sonner";
import {
  Send, X, Menu, Sparkles, ShoppingBasket, Sun, Leaf, Soup, Sprout,
  CupSoda, NotebookPen, MessageSquarePlus, Trash2, MessageCircle, CalendarDays,
} from "lucide-react";

const ABILITIES = [
  { icon: Leaf, title: "Herbal Wisdom", text: "Gentle remedies, tisanes and the old virtues of every herb." },
  { icon: Soup, title: "Cottage Meal Planner", text: "A week of dinners planned around what's in your larder." },
  { icon: Sprout, title: "Garden Planning", text: "What to sow, plant and tend as the seasons turn." },
  { icon: ShoppingBasket, title: "Pantry & Shopping", text: "Agnes keeps your larder and builds your shopping list." },
  { icon: NotebookPen, title: "Recipe Generator", text: "A fresh, doable recipe whenever inspiration runs dry." },
  { icon: CupSoda, title: "Tea Recommendations", text: "The perfect brew for the hour, the weather or the mood." },
];

const CHIPS = [
  { label: "Plan tonight's dinner", mode: "meal-planner", text: "What could I make for dinner tonight?" },
  { label: "A recipe from my pantry", mode: "recipe", text: "Turn what's in my pantry into a lovely recipe." },
  { label: "A gentle herbal remedy", mode: "herbs", text: "I'm feeling a little run down — is there a gentle herbal remedy you'd suggest?" },
  { label: "Which tea this evening?", mode: "tea", text: "Recommend a soothing tea for this evening." },
  { label: "Plan my garden", mode: "garden", text: "Help me plan what to sow and tend in my garden this month." },
];

const MD = {
  p: (p) => <p className="mb-3 leading-relaxed" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 mb-3 space-y-1" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="font-semibold text-brand-charcoal" {...p} />,
  h1: (p) => <h3 className="font-serif text-2xl mt-2 mb-3" {...p} />,
  h2: (p) => <h3 className="font-serif text-xl mt-2 mb-2" {...p} />,
  h3: (p) => <h4 className="font-serif text-lg mt-2 mb-2" {...p} />,
  hr: () => <hr className="my-4 border-brand-linen" />,
  a: (p) => <a className="text-brand-copper underline" {...p} />,
  em: (p) => <em className="italic" {...p} />,
};

/* ---------------- Whisper card ---------------- */
function WhisperCard({ whisper, onToggleEmail }) {
  if (!whisper?.text) return null;
  return (
    <div className="bg-brand-warm-white border border-brand-linen p-6 text-left mb-10 max-w-2xl mx-auto" data-testid="agnes-whisper">
      <div className="flex items-center gap-2 mb-3">
        <Sun size={16} className="text-brand-gold" />
        <span className="overline text-[0.58rem]">A whisper from Agnes</span>
      </div>
      <p className="font-serif text-lg sm:text-xl leading-relaxed text-brand-charcoal italic">{whisper.text}</p>
      {whisper.recipe_slug && (
        <Link to={`/daily-recipe/${whisper.recipe_slug}`} data-testid="whisper-recipe-link"
          className="inline-flex items-center gap-2 mt-4 font-sans text-[0.68rem] uppercase tracking-[0.14em] text-brand-copper hover:text-brand-charcoal">
          Today's recipe · {whisper.recipe_title} →
        </Link>
      )}
      <label className="flex items-center gap-3 mt-5 pt-4 border-t border-brand-linen cursor-pointer" data-testid="whisper-email-toggle">
        <button type="button" onClick={onToggleEmail} role="switch" aria-checked={!!whisper.email_opt_in}
          className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${whisper.email_opt_in ? "bg-brand-sage" : "bg-brand-linen"}`}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-brand-cream transition-all ${whisper.email_opt_in ? "left-[22px]" : "left-0.5"}`} />
        </button>
        <span className="font-body text-sm text-brand-muted">Send this to me each morning by email</span>
      </label>
    </div>
  );
}

/* ---------------- Paywall ---------------- */
function Paywall({ user, onSubscribe, loading }) {
  return (
    <div data-testid="agnes-paywall">
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-16 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Overline>Your cottage companion</Overline>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light mt-5 mb-6 leading-[0.95]">Ask Agnes<span className="text-brand-gold">.</span></h1>
            <p className="font-body text-brand-body text-lg leading-relaxed max-w-xl">
              A little of my lifetime's knowing, always at your side. Ask me about herbs and remedies, let me plan
              your week's meals around your larder, help lay out your garden, keep your pantry and shopping list,
              dream up a recipe, or brew you just the right pot of tea — all in one gentle conversation.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-5xl text-brand-charcoal">$5</span>
                <span className="font-body text-brand-muted">/ month</span>
              </div>
              <p className="font-sans text-[0.68rem] uppercase tracking-[0.14em] text-brand-muted">Cancel anytime · Included free with Membership</p>
            </div>
            <div className="mt-8">
              {user ? (
                <PrimaryButton onClick={onSubscribe} testid="agnes-subscribe-btn" disabled={loading}>
                  {loading ? "Opening checkout…" : "Begin for $5/month"}
                </PrimaryButton>
              ) : (
                <p className="font-body text-brand-muted italic">Create your free account below to begin.</p>
              )}
            </div>
          </div>
          <div className="relative">
            <div className="overflow-hidden border border-brand-linen shadow-xl">
              <img src={IMAGES.agnesAi} alt="A cottage kitchen table with tea, herbs and a recipe journal" className="w-full aspect-[4/3] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-brand-cream border border-brand-linen px-6 py-4 shadow-lg hidden sm:block">
              <p className="font-serif text-xl italic text-brand-olive">"From my cottage to yours."</p>
            </div>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 border-t border-brand-linen">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {ABILITIES.map((a) => (
            <div key={a.title} className="flex gap-4">
              <a.icon size={26} className="text-brand-sage shrink-0 mt-1" strokeWidth={1.4} />
              <div>
                <h3 className="font-serif text-2xl leading-snug">{a.title}</h3>
                <p className="font-body text-brand-muted mt-1 leading-relaxed">{a.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      {!user && (
        <section className="max-w-md mx-auto px-6 pb-24">
          <AuthForm intro={<p className="font-body text-brand-body mb-6 text-center">Create your free account, then begin your subscription.</p>} />
        </section>
      )}
    </div>
  );
}

/* ---------------- Chat ---------------- */
function ChatApp({ pantry, setPantry, refreshShopping, whisper, onToggleWhisperEmail, seed, clearSeed }) {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const threadRef = useRef(null);

  const loadSessions = useCallback(async () => { try { setSessions(await agnesListSessions()); } catch {} }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [messages, sending]);

  const openSession = async (sid) => {
    setActiveId(sid); setSidebarOpen(false);
    try { const res = await agnesGetSession(sid); setMessages(res.messages || []); } catch { setMessages([]); }
  };
  const newChat = () => { setActiveId(null); setMessages([]); setInput(""); setSidebarOpen(false); };
  const removeSession = async (sid, e) => {
    e.stopPropagation();
    try { await agnesDeleteSession(sid); setSessions((s) => s.filter((x) => x.id !== sid)); if (sid === activeId) newChat(); } catch {}
  };

  const send = async (text, mode, forceNew) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput("");
    const sessionId = forceNew ? null : activeId;
    if (forceNew) setActiveId(null);
    setMessages((m) => (forceNew ? [] : m).concat({ role: "user", content }));
    setSending(true);
    try {
      const res = await agnesChat({ message: content, session_id: sessionId, mode: mode || null });
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      setActiveId(res.session_id);
      if (res.pantry_changed && res.pantry) { setPantry(res.pantry); toast.success("Agnes tidied your pantry."); }
      if (res.shopping_added > 0) { refreshShopping(); toast.success(`Agnes added ${res.shopping_added} item${res.shopping_added > 1 ? "s" : ""} to your shopping list.`); }
      loadSessions();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMessages((m) => [...m, { role: "assistant", content: typeof d === "string" ? d : "Forgive me, dear — I lost my thread. Do ask again." }]);
    } finally { setSending(false); }
  };

  // Send a seeded message (e.g. "refine this meal plan") in a fresh conversation
  useEffect(() => {
    if (seed) { send(seed, null, true); clearSeed(); }
  }, [seed]); // eslint-disable-line

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="max-w-7xl mx-auto w-full h-full flex" data-testid="agnes-chat-app">
      {sidebarOpen && <div className="fixed inset-0 bg-brand-charcoal/40 z-[55] lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static top-0 left-0 z-[56] h-full w-72 bg-brand-warm-white border-r border-brand-linen flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-4 shrink-0">
          <button onClick={newChat} data-testid="agnes-new-chat" className="w-full flex items-center justify-center gap-2 bg-brand-olive text-brand-cream py-3 font-sans text-xs uppercase tracking-[0.14em] hover:bg-brand-charcoal transition-colors">
            <MessageSquarePlus size={16} /> New conversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          <span className="overline text-[0.58rem] px-2">Your conversations</span>
          <ul className="mt-2 space-y-1" data-testid="agnes-session-list">
            {sessions.map((s) => (
              <li key={s.id}>
                <button onClick={() => openSession(s.id)} data-testid={`agnes-session-${s.id}`}
                  className={`w-full group flex items-center gap-2 text-left px-3 py-2.5 transition-colors ${activeId === s.id ? "bg-brand-linen" : "hover:bg-brand-cream"}`}>
                  <span className="flex-1 font-body text-sm text-brand-body truncate">{s.title || "New conversation"}</span>
                  <Trash2 size={14} onClick={(e) => removeSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-brand-muted hover:text-brand-copper shrink-0" />
                </button>
              </li>
            ))}
            {sessions.length === 0 && <li className="px-3 py-4 font-body text-sm text-brand-muted italic">No conversations yet.</li>}
          </ul>
        </div>
      </aside>

      <section className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center gap-3 h-12 px-4 border-b border-brand-linen shrink-0 lg:hidden">
          <button onClick={() => setSidebarOpen(true)} data-testid="agnes-sidebar-toggle" className="text-brand-charcoal flex items-center gap-2">
            <Menu size={20} /> <span className="font-sans text-xs uppercase tracking-[0.12em]">Conversations</span>
          </button>
        </div>

        <div ref={threadRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-8" data-testid="agnes-thread">
          {messages.length === 0 ? (
            <div className="max-w-2xl mx-auto pt-2 text-center">
              <WhisperCard whisper={whisper} onToggleEmail={onToggleWhisperEmail} />
              <Sparkles size={28} className="mx-auto text-brand-gold mb-4" strokeWidth={1.4} />
              <h2 className="font-serif text-3xl sm:text-4xl mb-3">Good day, dear. How can I help?</h2>
              <p className="font-body text-brand-muted leading-relaxed mb-8">Ask me anything — or begin with one of these.</p>
              <div className="flex flex-wrap justify-center gap-3">
                {CHIPS.map((c) => (
                  <button key={c.label} onClick={() => send(c.text, c.mode)} data-testid={`agnes-chip-${c.mode}`}
                    className="border border-brand-linen bg-brand-warm-white px-5 py-2.5 font-body text-sm text-brand-body hover:border-brand-gold hover:text-brand-charcoal transition-colors">
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`agnes-msg-${m.role}`}>
                  {m.role === "user" ? (
                    <div className="bg-brand-olive text-brand-cream px-5 py-3 max-w-[80%] font-body leading-relaxed rounded-t-2xl rounded-bl-2xl">{m.content}</div>
                  ) : (
                    <div className="max-w-[92%] sm:max-w-[80%]">
                      <span className="overline text-[0.58rem] text-brand-copper">Agnes</span>
                      <div className="font-body text-brand-body mt-1"><ReactMarkdown components={MD}>{m.content}</ReactMarkdown></div>
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="agnes-typing">
                  <div className="flex gap-1.5 items-center py-2">
                    {[0, 1, 2].map((d) => <span key={d} className="w-2 h-2 rounded-full bg-brand-sage animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-brand-linen px-4 sm:px-8 py-4 shrink-0 bg-brand-cream">
          <div className="max-w-3xl mx-auto flex items-end gap-3">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1}
              data-testid="agnes-input" placeholder="Ask Agnes anything…"
              className="flex-1 resize-none bg-brand-warm-white border border-brand-linen px-4 py-3 font-body text-brand-charcoal focus:outline-none focus:border-brand-gold max-h-32" />
            <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="agnes-send-btn"
              className="bg-brand-olive text-brand-cream w-12 h-12 flex items-center justify-center hover:bg-brand-charcoal transition-colors disabled:opacity-40 shrink-0">
              <Send size={18} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function AgnesAI() {
  const { user, ready, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [pantry, setPantry] = useState([]);
  const [shopping, setShopping] = useState([]);
  const [garden, setGarden] = useState([]);
  const [whisper, setWhisper] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("pantry");
  const [view, setView] = useState("chat");
  const [seed, setSeed] = useState(null);

  const hasAgnes = user && user.has_agnes;
  const refreshShopping = useCallback(() => { agnesGetShopping().then(setShopping).catch(() => {}); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    (async () => {
      try { await paymentStatus(sid); } catch {}
      await refresh();
      toast.success("Welcome — Agnes is ready for you.");
      params.delete("session_id"); setParams(params, { replace: true });
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (hasAgnes) {
      agnesGetPantry().then(setPantry).catch(() => {});
      agnesGetShopping().then(setShopping).catch(() => {});
      agnesGetGarden().then(setGarden).catch(() => {});
      agnesGetWhisper().then(setWhisper).catch(() => {});
    }
  }, [hasAgnes]);

  const doCheckout = async (name, email) => {
    setLoading(true);
    try {
      const res = await checkout({ lookup_key: "agnes-ai", origin_url: window.location.origin, name, email });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open checkout. Please try again."); setLoading(false); }
  };
  const subscribe = () => setCheckoutOpen(true);

  const openLarder = (tab) => { setDrawerTab(tab || "pantry"); setDrawerOpen(true); };

  const toggleWhisperEmail = async () => {
    const next = !whisper?.email_opt_in;
    setWhisper((w) => ({ ...w, email_opt_in: next }));
    try { await agnesSetWhisperEmail(next); toast.success(next ? "Agnes will write to you each morning." : "Morning emails turned off."); }
    catch { setWhisper((w) => ({ ...w, email_opt_in: !next })); toast.error("Could not update your preference."); }
  };

  const refine = (plan) => {
    const dayList = (plan.days || []).map((d) => `${d.day}: ${d.meal}`).join("; ");
    setSeed(`Here's my meal plan "${plan.title}" — ${dayList}. Could you help me tweak it?`);
    setView("chat");
  };

  if (!ready) return <div className="min-h-[60vh] flex items-center justify-center font-body text-brand-muted">Loading…</div>;

  if (!hasAgnes) return (
    <div data-testid="agnes-page">
      <Paywall user={user} onSubscribe={subscribe} loading={loading} />
      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} onConfirm={doCheckout} submitting={loading} title="Add AgnesAI" />
    </div>
  );

  const TABS = [["chat", "Chat", MessageCircle], ["meals", "Meal Plans", CalendarDays]];

  return (
    <div data-testid="agnes-page">
      <div className="flex flex-col" style={{ height: "calc(100vh - 5rem)" }}>
        <div className="shrink-0 border-b border-brand-linen bg-brand-cream">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center gap-1 h-12">
            {TABS.map(([k, label, Icon]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`agnes-tab-${k}`}
                className={`flex items-center gap-2 px-4 py-2 font-sans text-[0.72rem] uppercase tracking-[0.12em] transition-colors ${view === k ? "text-brand-charcoal border-b-2 border-brand-gold" : "text-brand-muted hover:text-brand-charcoal"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
            <button onClick={() => openLarder("pantry")} data-testid="open-larder-btn"
              className="ml-auto flex items-center gap-2 border border-brand-gold text-brand-charcoal px-4 py-2 font-sans text-[0.7rem] uppercase tracking-[0.12em] hover:bg-brand-linen transition-colors">
              <ShoppingBasket size={15} /> Larder & List{shopping.length ? ` · ${shopping.length}` : ""}
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {view === "chat"
            ? <ChatApp pantry={pantry} setPantry={setPantry} refreshShopping={refreshShopping} whisper={whisper}
                onToggleWhisperEmail={toggleWhisperEmail} seed={seed} clearSeed={() => setSeed(null)} />
            : <MealPlans setShopping={setShopping} openShopping={() => openLarder("shopping")} onRefine={refine}
                digestOptIn={!!whisper?.digest_opt_in} setDigestOptIn={(v) => setWhisper((w) => ({ ...w, digest_opt_in: v }))} />}
        </div>
      </div>

      <PantryDrawer open={drawerOpen} setOpen={setDrawerOpen} tab={drawerTab} setTab={setDrawerTab}
        pantry={pantry} setPantry={setPantry} shopping={shopping} setShopping={setShopping}
        garden={garden} setGarden={setGarden} />
    </div>
  );
}
